import type { Mm2ModPack } from "./Mm2DiscPatch";
import { MM2_USA_CHARACTER1_FACE1 } from "./Mm2AppearanceMap";

const FACE1_PACKAGE_OFFSET = MM2_USA_CHARACTER1_FACE1.pixelPayloadOffset!;

// Runtime safety note:
// Face 1's payload mapping is confirmed, but the first full 64x64 replacement
// caused New Game to stall at the initial loading screen. Until the texture's
// complete transfer/CLUT constraints are proven, keep the shipped probe bounded
// and validate every byte we touch against the known USA image.
const FACE1_EXPECTED_PREFIX = new Uint8Array(32).fill(0x11);

// v22 advances one more 32-byte stripe beyond the deployed v21 octet. Every
// byte remains exact-original guarded, so this probe cannot silently write into
// an unexpected image. The nine stripes total 288 bytes / 576 PSMT4 pixels while
// staying far short of the earlier unsafe full 2048-byte replacement.
const FACE1_SECOND_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_THIRD_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_FOURTH_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_FIFTH_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_SIXTH_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_SEVENTH_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_EIGHTH_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);
const FACE1_NINTH_STRIPE_EXPECTED = new Uint8Array(32).fill(0x11);

function makeFaceStripe(): Uint8Array {
  return new Uint8Array(32).fill(0xee);
}

export const genieVisualPack: Mm2ModPack = {
  id: "mm2-usa-geniewoman-face-bootsafe-v22",
  name: "GenieWoman Face Probe v22",
  description: "Boot-safe Face 1 expansion: nine exact-original-validated 64-pixel PSMT4 stripes, extending the deployed v21 probe without approaching the unsafe full-payload rewrite.",
  patches: [
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET,
      bytes: makeFaceStripe(),
      expected: FACE1_EXPECTED_PREFIX,
      label: `GenieWoman Face 1 stripe A v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 32,
      bytes: makeFaceStripe(),
      expected: FACE1_SECOND_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe B v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 64,
      bytes: makeFaceStripe(),
      expected: FACE1_THIRD_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe C v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 96,
      bytes: makeFaceStripe(),
      expected: FACE1_FOURTH_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe D v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 128,
      bytes: makeFaceStripe(),
      expected: FACE1_FIFTH_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe E v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 160,
      bytes: makeFaceStripe(),
      expected: FACE1_SIXTH_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe F v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 192,
      bytes: makeFaceStripe(),
      expected: FACE1_SEVENTH_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe G v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 224,
      bytes: makeFaceStripe(),
      expected: FACE1_EIGHTH_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe H v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
    {
      file: "PACKAGE.BIN",
      offset: FACE1_PACKAGE_OFFSET + 256,
      bytes: makeFaceStripe(),
      expected: FACE1_NINTH_STRIPE_EXPECTED,
      label: `GenieWoman Face 1 stripe I v22 (TBP0 0x${MM2_USA_CHARACTER1_FACE1.tbp0!.toString(16).toUpperCase()})`,
    },
  ],
};

export const faceMarkerPack = genieVisualPack;
